import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { 
  LayoutDashboard, Package, Briefcase, Calendar, Wrench, ClipboardList, 
  ShoppingBag, Plus, Trash2, Edit2, Save, X, Upload, Image, FileText, Loader2, Sparkles, Check
} from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const prepareImage = async (file) => {
  if (!file.type.startsWith("image/")) throw new Error("Seleziona immagini JPG, PNG o WebP");
  const bitmap = await createImageBitmap(file);
  try {
    if (Math.max(bitmap.width, bitmap.height) <= 1920 && file.size <= 1024 * 1024) return file;
    const ratio = Math.min(1, 1920 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * ratio));
    canvas.height = Math.max(1, Math.round(bitmap.height * ratio));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/webp", 0.85));
    if (!blob) throw new Error("Impossibile preparare l'immagine");
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".webp", { type: "image/webp" });
  } finally {
    bitmap.close();
  }
};

const categories = [
  { id: "infissi", name: "Infissi" },
  { id: "porte", name: "Porte" },
  { id: "cucine", name: "Cucine" },
  { id: "arredamento", name: "Arredamento" },
  { id: "pergole", name: "Pergole" },
  { id: "parquet", name: "Parquet" },
  { id: "pavimenti", name: "Pavimenti" },
  { id: "outdoor", name: "Outdoor" },
];

// File Upload Component
const FileUploader = ({ onUpload, onBusyChange, multiple = false, accept = "image/*", label = "Carica File" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { token } = useAuth();

  const handleFileChange = async (e) => {
    let files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    onBusyChange?.(true);
    try {
      if (files.length > 10) throw new Error("Carica al massimo 10 immagini per volta");
      if (!accept.includes("pdf")) files = await Promise.all(files.map(prepareImage));
      if (files.some(file => file.size > 5 * 1024 * 1024)) throw new Error("Ogni file deve essere al massimo 5 MB");
      if (multiple && files.length > 1) {
        // Multiple images upload
        const formData = new FormData();
        files.forEach(file => formData.append("files", file));
        
        const response = await axios.post(`${API}/upload/multiple-images`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data.count !== files.length || response.data.uploaded?.length !== files.length) throw new Error("Caricamento incompleto: riprova");
        const urls = response.data.uploaded.map(f => `${BACKEND_URL}${f.url}`);
        onUpload(urls);
        toast.success(`${response.data.count} immagini caricate`);
      } else {
        // Single file upload
        const file = files[0];
        const formData = new FormData();
        formData.append("file", file);
        
        const endpoint = accept.includes("pdf") ? "/upload/document" : "/upload/image";
        const response = await axios.post(`${API}${endpoint}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.data.url) throw new Error("Il file non e' stato salvato");
        const url = `${BACKEND_URL}${response.data.url}`;
        onUpload(multiple ? [url] : url);
        toast.success("File caricato");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Errore nel caricamento");
    } finally {
      setUploading(false);
      onBusyChange?.(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        className="hidden"
        id={`file-upload-${label}`}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full border-dashed border-zinc-600 text-white hover:bg-zinc-800 h-12"
      >
        {uploading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : accept.includes("pdf") ? (
          <FileText className="mr-2 h-4 w-4" />
        ) : (
          <Image className="mr-2 h-4 w-4" />
        )}
        {uploading ? "Caricamento..." : label}
      </Button>
    </div>
  );
};

// Image Preview Component
const ImagePreview = ({ images, onRemove }) => {
  if (!images || images.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {images.map((img, idx) => (
        <div key={idx} className="relative group">
          <img 
            src={img} 
            alt={`Preview ${idx}`} 
            className="w-16 h-16 object-cover rounded-lg border border-zinc-700"
          />
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
};

const AdminPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Products
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({
    name: "", description: "", category: "", price: "", discount_price: "",
    images: [], documents: [], is_outlet: false, is_available: true, stock: 0
  });
  
  // Projects
  const [projects, setProjects] = useState([]);
  const [activeUploads, setActiveUploads] = useState(0);
  const [savingProject, setSavingProject] = useState(false);
  const [descriptionPrompt, setDescriptionPrompt] = useState("");
  const [generatedDescription, setGeneratedDescription] = useState("");
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const handleUploadBusy = uploading => setActiveUploads(count => Math.max(0, count + (uploading ? 1 : -1)));
  const [newProject, setNewProject] = useState({
    title: "", description: "", category: "", images: [], is_featured: false
  });

  // Appointments, Quotes, Assistance, Orders
  const [appointments, setAppointments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [assistance, setAssistance] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookingCalendars, setBookingCalendars] = useState([]);
  const [serviceAccountEmail, setServiceAccountEmail] = useState("");
  const [savingCalendars, setSavingCalendars] = useState(false);
  const saveCalendars = async () => {
    setSavingCalendars(true);
    try {
      const response = await axios.put(`${API}/admin/calendars`, { calendars: bookingCalendars }, { headers: { Authorization: `Bearer ${token}` } });
      setBookingCalendars(response.data.calendars);
      toast.success("Calendari aggiornati");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingCalendars(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [token]);

  const fetchAll = async () => {
    try {
      const [statsRes, productsRes, projectsRes, appointmentsRes, quotesRes, assistanceRes, ordersRes, calendarsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assistance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/calendars`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setProjects(projectsRes.data);
      setAppointments(appointmentsRes.data);
      setQuotes(quotesRes.data);
      setAssistance(assistanceRes.data);
      setOrders(ordersRes.data);
      setBookingCalendars(calendarsRes.data.calendars);
      setServiceAccountEmail(calendarsRes.data.service_account_email);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Product handlers
  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.category || !newProduct.price) {
      toast.error("Compila nome, categoria e prezzo");
      return;
    }
    
    try {
      const productData = {
        name: newProduct.name,
        description: newProduct.description,
        category: newProduct.category,
        price: parseFloat(newProduct.price),
        discount_price: newProduct.discount_price ? parseFloat(newProduct.discount_price) : null,
        images: newProduct.images,
        is_outlet: newProduct.is_outlet,
        is_available: newProduct.is_available,
        stock: parseInt(newProduct.stock) || 0
      };
      await axios.post(`${API}/products`, productData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prodotto creato!");
      setNewProduct({ 
        name: "", description: "", category: "", price: "", discount_price: "", 
        images: [], documents: [], is_outlet: false, is_available: true, stock: 0 
      });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prodotto eliminato");
      fetchAll();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Project handlers
  const handleGenerateDescription = async () => {
    if (!descriptionPrompt.trim() || generatingDescription) return;
    setGeneratingDescription(true);
    try {
      const response = await axios.post(`${API}/admin/project-description`, {
        prompt: descriptionPrompt.trim(),
        title: newProject.title,
        category: categories.find(category => category.id === newProject.category)?.name || "",
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      setGeneratedDescription(response.data.description);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generazione non riuscita. Riprova tra poco.");
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleCreateProject = async () => {
    if (activeUploads || savingProject || generatingDescription) return;
    if (!newProject.title || !newProject.category) {
      toast.error("Compila titolo e categoria");
      return;
    }
    
    setSavingProject(true);
    try {
      const projectData = {
        title: newProject.title,
        description: newProject.description,
        category: newProject.category,
        images: newProject.images,
        is_featured: newProject.is_featured
      };
      const response = await axios.post(`${API}/projects`, projectData, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(prev => [response.data, ...prev]);
      toast.success("Progetto creato!");
      setNewProject({ title: "", description: "", category: "", images: [], is_featured: false });
      setDescriptionPrompt("");
      setGeneratedDescription("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Progetto eliminato");
      fetchAll();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Image handlers for products
  const handleProductImagesUpload = (urls) => {
    const newImages = Array.isArray(urls) ? urls : [urls];
    setNewProduct(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const handleRemoveProductImage = (index) => {
    setNewProduct(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Image handlers for projects
  const handleProjectImagesUpload = (urls) => {
    const newImages = Array.isArray(urls) ? urls : [urls];
    setNewProject(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }));
  };

  const handleRemoveProjectImage = (index) => {
    setNewProject(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Status update handlers
  const updateStatus = async (type, id, status) => {
    try {
      await axios.put(`${API}/${type}/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Stato aggiornato");
      fetchAll();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const StatusSelect = ({ type, id, currentStatus }) => {
    const [open, setOpen] = React.useState(false);
    
    const handleValueChange = (v) => {
      setOpen(false);
      updateStatus(type, id, v);
    };
    
    return (
      <Select value={currentStatus} onValueChange={handleValueChange} open={open} onOpenChange={setOpen}>
        <SelectTrigger className="w-32 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem key={`${id}-pending`} value="pending" className="text-yellow-500">In attesa</SelectItem>
          <SelectItem key={`${id}-confirmed`} value="confirmed" className="text-emerald-500">Confermato</SelectItem>
          <SelectItem key={`${id}-completed`} value="completed" className="text-blue-500">Completato</SelectItem>
          <SelectItem key={`${id}-cancelled`} value="cancelled" className="text-red-500">Annullato</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black p-4 space-y-4">
        <Skeleton className="h-8 w-48 bg-zinc-800" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-24 bg-zinc-800 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-20" data-testid="admin-page">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <h1 className="text-xl font-light text-white tracking-wide">Pannello Admin</h1>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-zinc-900 p-4 rounded-lg border border-white/5">
            <p className="text-2xl font-bold text-white">{stats.users}</p>
            <p className="text-xs text-white/60">Utenti</p>
          </div>
          <div className="bg-zinc-900 p-4 rounded-lg border border-white/5">
            <p className="text-2xl font-bold text-white">{stats.products}</p>
            <p className="text-xs text-white/60">Prodotti</p>
          </div>
          <div className="bg-zinc-900 p-4 rounded-lg border border-white/5">
            <p className="text-2xl font-bold text-yellow-500">{stats.pending_appointments}</p>
            <p className="text-xs text-white/60">Appuntamenti</p>
          </div>
          <div className="bg-zinc-900 p-4 rounded-lg border border-white/5">
            <p className="text-2xl font-bold text-yellow-500">{stats.pending_orders}</p>
            <p className="text-xs text-white/60">Ordini</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="products" className="w-full">
        <TabsList className="w-full bg-zinc-900 rounded-none border-b border-white/10 h-auto p-0 flex overflow-x-auto">
          <TabsTrigger value="products" className="flex-1 py-3 rounded-none text-xs data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500">
            <Package size={16} className="mr-1" /> Prodotti
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex-1 py-3 rounded-none text-xs data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500">
            <Briefcase size={16} className="mr-1" /> Progetti
          </TabsTrigger>
          <TabsTrigger value="appointments" className="flex-1 py-3 rounded-none text-xs data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500">
            <Calendar size={16} className="mr-1" /> Appunt.
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex-1 py-3 rounded-none text-xs data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500">
            <ShoppingBag size={16} className="mr-1" /> Ordini
          </TabsTrigger>
          <TabsTrigger value="calendars" className="flex-1 py-3 rounded-none text-xs data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500">
            <Calendar size={16} className="mr-1" /> Calendari
          </TabsTrigger>
        </TabsList>

        <TabsContent value="calendars" className="p-4 space-y-5">
          <h3 className="text-sm font-medium text-white">Calendari prenotabili</h3>
          {serviceAccountEmail && <div className="space-y-1">
            <Label className="text-white/60">Condividi il calendario con</Label>
            <p className="text-sm text-emerald-400 break-all">{serviceAccountEmail}</p>
          </div>}
          <div className="space-y-4">
            {bookingCalendars.map((calendar, index) => (
              <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_5rem_auto] gap-3 border-b border-white/10 pb-4">
                <div className="space-y-1">
                  <Label htmlFor={`calendar-name-${index}`} className="text-white/70">Nome</Label>
                  <Input id={`calendar-name-${index}`} value={calendar.name} maxLength={100} onChange={event => setBookingCalendars(list => list.map((item, i) => i === index ? { ...item, name: event.target.value } : item))} className="bg-zinc-900 border-zinc-700 text-white" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`calendar-id-${index}`} className="text-white/70">ID calendario</Label>
                  <Input id={`calendar-id-${index}`} value={calendar.id} maxLength={250} onChange={event => setBookingCalendars(list => list.map((item, i) => i === index ? { ...item, id: event.target.value } : item))} className="bg-zinc-900 border-zinc-700 text-white" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`calendar-priority-${index}`} className="text-white/70">Priorità</Label>
                  <Input id={`calendar-priority-${index}`} type="number" min="1" max="100" value={calendar.priority || index + 1} onChange={event => setBookingCalendars(list => list.map((item, i) => i === index ? { ...item, priority: event.target.value } : item))} className="bg-zinc-900 border-zinc-700 text-white" />
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <label className="flex items-center gap-2 text-sm text-white/70"><Switch checked={calendar.enabled !== false} onCheckedChange={enabled => setBookingCalendars(list => list.map((item, i) => i === index ? { ...item, enabled } : item))} /> Attivo</label>
                  <Button type="button" variant="ghost" size="icon" title="Rimuovi calendario" aria-label={`Rimuovi calendario ${calendar.name || index + 1}`} onClick={() => setBookingCalendars(list => list.filter((_, i) => i !== index))} className="text-red-400"><Trash2 size={16} /></Button>
                </div>
                <fieldset className="sm:col-span-4">
                  <legend className="text-sm text-white/70 mb-2">Prodotti gestiti</legend>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {categories.map(category => (
                      <label key={category.id} className="flex items-center gap-2 text-sm text-white/80">
                        <input type="checkbox" className="accent-emerald-500 h-4 w-4 shrink-0" checked={(calendar.product_types || categories.map(item => item.id)).includes(category.id)} onChange={event => {
                          const checked = event.target.checked;
                          setBookingCalendars(list => list.map((item, i) => {
                            if (i !== index) return item;
                            const selected = item.product_types || categories.map(option => option.id);
                            return { ...item, product_types: checked ? [...selected, category.id] : selected.filter(id => id !== category.id) };
                          }));
                        }} />
                        {category.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" disabled={bookingCalendars.length >= 20 || savingCalendars} onClick={() => setBookingCalendars(list => [...list, { id: "", name: "", enabled: true, priority: list.length + 1, product_types: [] }])} className="border-zinc-700 text-white"><Plus size={16} className="mr-2" /> Aggiungi calendario</Button>
            <Button type="button" disabled={savingCalendars} onClick={saveCalendars} className="bg-emerald-500 text-black hover:bg-emerald-400"><Save size={16} className="mr-2" /> {savingCalendars ? "Salvataggio..." : "Salva calendari"}</Button>
          </div>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="p-4 space-y-4">
          {/* New Product Form */}
          <div className="bg-zinc-900 p-4 rounded-lg border border-white/10 space-y-4">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Plus size={16} /> Nuovo Prodotto
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <Input 
                placeholder="Nome prodotto" 
                value={newProduct.name} 
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} 
                className="bg-zinc-800 border-zinc-700 text-white h-10" 
              />
              <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-10">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                  {categories.map((cat) => (
                    <SelectItem key={`product-cat-${cat.id}`} value={cat.id} className="text-white">{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input 
                type="number" 
                placeholder="Prezzo (€)" 
                value={newProduct.price} 
                onChange={(e) => setNewProduct({...newProduct, price: e.target.value})} 
                className="bg-zinc-800 border-zinc-700 text-white h-10" 
              />
              <Input 
                type="number" 
                placeholder="Prezzo Scontato (€)" 
                value={newProduct.discount_price} 
                onChange={(e) => setNewProduct({...newProduct, discount_price: e.target.value})} 
                className="bg-zinc-800 border-zinc-700 text-white h-10" 
              />
              <Input 
                type="number" 
                placeholder="Quantità in stock" 
                value={newProduct.stock} 
                onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})} 
                className="bg-zinc-800 border-zinc-700 text-white h-10" 
              />
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-white text-sm">
                  <Switch checked={newProduct.is_outlet} onCheckedChange={(v) => setNewProduct({...newProduct, is_outlet: v})} />
                  Outlet
                </label>
                <label className="flex items-center gap-2 text-white text-sm">
                  <Switch checked={newProduct.is_available} onCheckedChange={(v) => setNewProduct({...newProduct, is_available: v})} />
                  Disponibile
                </label>
              </div>
            </div>
            
            <Textarea 
              placeholder="Descrizione del prodotto" 
              value={newProduct.description} 
              onChange={(e) => setNewProduct({...newProduct, description: e.target.value})} 
              className="bg-zinc-800 border-zinc-700 text-white" 
            />
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Immagini Prodotto</Label>
              <FileUploader 
                onUpload={handleProductImagesUpload}
                onBusyChange={handleUploadBusy}
                multiple={true}
                accept="image/*"
                label="Carica Immagini (JPG, PNG, WebP)"
              />
              <ImagePreview 
                images={newProduct.images} 
                onRemove={handleRemoveProductImage}
              />
            </div>
            
            <Button onClick={handleCreateProduct} className="bg-emerald-500 text-black hover:bg-emerald-400 w-full">
              <Save size={16} className="mr-2" /> Crea Prodotto
            </Button>
          </div>

          {/* Products List */}
          <div className="space-y-2">
            <h4 className="text-sm text-white/60 mb-3">Prodotti esistenti ({products.length})</h4>
            {products.map((product) => (
              <div key={product.id} className="bg-zinc-900 p-3 rounded-lg border border-white/5 flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                  <img 
                    src={product.images?.[0] || "/brand/colacicco-profilo-nero-piccolo.png"}
                    alt="" 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{product.name}</p>
                  <p className="text-xs text-white/60">{product.category}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {product.discount_price ? (
                      <>
                        <span className="text-emerald-500 font-bold text-sm">€{product.discount_price}</span>
                        <span className="text-white/40 text-xs line-through">€{product.price}</span>
                      </>
                    ) : (
                      <span className="text-white font-bold text-sm">€{product.price}</span>
                    )}
                    {product.is_outlet && (
                      <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">OUTLET</span>
                    )}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteProduct(product.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="p-4 space-y-4">
          {/* New Project Form */}
          <div className="bg-zinc-900 p-4 rounded-lg border border-white/10 space-y-4">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Plus size={16} /> Nuovo Progetto (Lavoro Fatto)
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <Input 
                placeholder="Titolo progetto" 
                value={newProject.title} 
                onChange={(e) => setNewProject({...newProject, title: e.target.value})} 
                className="bg-zinc-800 border-zinc-700 text-white h-10" 
              />
              <Select value={newProject.category} onValueChange={(v) => setNewProject({...newProject, category: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-10">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                  {categories.map((cat) => (
                    <SelectItem key={`project-cat-${cat.id}`} value={cat.id} className="text-white">{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-ai-prompt" className="text-white/70">Brief per l'AI</Label>
              <Textarea
                id="project-ai-prompt"
                value={descriptionPrompt}
                onChange={event => setDescriptionPrompt(event.target.value)}
                maxLength={2500}
                disabled={generatingDescription}
                placeholder="Es: porte in rovere naturale per una casa luminosa. Tono elegante, valorizza le finiture e il lavoro su misura."
                className="bg-zinc-800 border-zinc-700 text-white min-h-[90px]"
              />
              <Button type="button" onClick={handleGenerateDescription} disabled={!descriptionPrompt.trim() || generatingDescription || savingProject} className="bg-emerald-500 text-black hover:bg-emerald-400">
                {generatingDescription ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
                {generatingDescription ? "Generazione in corso..." : "Genera descrizione"}
              </Button>
              {generatedDescription && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="project-ai-draft" className="text-white/70">Proposta</Label>
                  <Textarea id="project-ai-draft" value={generatedDescription} onChange={event => setGeneratedDescription(event.target.value)} className="bg-zinc-900 border-zinc-700 text-white min-h-[200px]" />
                  <Button type="button" variant="outline" disabled={!generatedDescription.trim() || generatingDescription} onClick={() => {
                    setNewProject(project => ({ ...project, description: generatedDescription.trim() }));
                    setGeneratedDescription("");
                    toast.success("Descrizione inserita");
                  }} className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10">
                    <Check size={16} className="mr-2" /> Usa descrizione
                  </Button>
                </div>
              )}
            </div>

            <Textarea 
              placeholder="Descrizione del progetto" 
              value={newProject.description} 
              onChange={(e) => setNewProject({...newProject, description: e.target.value})} 
              className="bg-zinc-800 border-zinc-700 text-white" 
            />
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Immagini Progetto</Label>
              <FileUploader 
                onUpload={handleProjectImagesUpload}
                onBusyChange={handleUploadBusy}
                multiple={true}
                accept="image/*"
                label="Carica Immagini (JPG, PNG, WebP)"
              />
              <ImagePreview 
                images={newProject.images} 
                onRemove={handleRemoveProjectImage}
              />
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-white text-sm">
                <Switch checked={newProject.is_featured} onCheckedChange={(v) => setNewProject({...newProject, is_featured: v})} />
                In evidenza
              </label>
            </div>
            
            <Button onClick={handleCreateProject} disabled={activeUploads > 0 || savingProject || generatingDescription} className="bg-emerald-500 text-black hover:bg-emerald-400 w-full">
              <Save size={16} className="mr-2" /> Crea Progetto
            </Button>
          </div>

          {/* Projects List */}
          <div className="space-y-2">
            <h4 className="text-sm text-white/60 mb-3">Progetti esistenti ({projects.length})</h4>
            {projects.map((project) => (
              <div key={project.id} className="bg-zinc-900 p-3 rounded-lg border border-white/5 flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                  <img 
                    src={project.images?.[0] || "/brand/colacicco-profilo-nero-piccolo.png"}
                    alt="" 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{project.title}</p>
                  <p className="text-xs text-white/60">{project.category} • {project.likes || 0} likes</p>
                  {project.is_featured && (
                    <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">IN EVIDENZA</span>
                  )}
                </div>
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteProject(project.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Appointments Tab */}
        <TabsContent value="appointments" className="p-4 space-y-2">
          {appointments.length === 0 ? (
            <p className="text-center text-white/60 py-8">Nessun appuntamento</p>
          ) : (
            appointments.map((apt) => (
              <div key={apt.id} className="bg-zinc-900 p-3 rounded-lg border border-white/5">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-white">{apt.user_name}</p>
                    <p className="text-xs text-white/60">{apt.date} • {apt.time}</p>
                    <p className="text-xs text-emerald-500">{apt.product_type}</p>
                    {apt.notes && <p className="text-xs text-white/40 mt-1">{apt.notes}</p>}
                  </div>
                  <StatusSelect type="appointments" id={apt.id} currentStatus={apt.status} />
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="p-4 space-y-2">
          {orders.length === 0 ? (
            <p className="text-center text-white/60 py-8">Nessun ordine</p>
          ) : (
            orders.map((order) => (
              <div key={order.id} className="bg-zinc-900 p-3 rounded-lg border border-white/5">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-white">Ordine #{order.id.slice(0, 8)}</p>
                    <p className="text-xs text-white/60">{order.items?.length || 0} prodotti</p>
                    <p className="text-emerald-500 font-bold">€{order.total?.toFixed(2)}</p>
                    <p className="text-xs text-white/40 mt-1">{order.shipping_address}</p>
                  </div>
                  <StatusSelect type="orders" id={order.id} currentStatus={order.status} />
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
